import { useState } from 'react'
import { supabase } from '../../../../lib/supabase'

export default function SubscriptionsTable({ members, settings, getSubscriptionStatus, onRecordPayment, onRefresh }) {
  const currency = settings.currency || 'ZMW'
  const [confirmModal, setConfirmModal] = useState(null)
  const [expanded, setExpanded] = useState(null)

  const statusStyles = {
    paid: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    unpaid: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    expired: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
  }

  const getLatestSubscription = (member) => {
    if (!member.subscriptions || member.subscriptions.length === 0) return null
    return member.subscriptions.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date))[0]
  }

  const handleConfirm = async () => {
    if (!confirmModal) return
    if (confirmModal.type === 'delete') {
      await supabase.from('subscriptions').delete().eq('id', confirmModal.subscriptionId)
    }
    if (confirmModal.type === 'inactive') {
      await supabase.from('subscriptions').update({ status: 'inactive', valid_until: new Date().toISOString() }).eq('id', confirmModal.subscriptionId)
    }
    setConfirmModal(null)
    setExpanded(null)
    onRefresh()
  }

  if (members.length === 0) {
    return <div className="text-gray-500 text-sm uppercase tracking-widest text-center py-10">No members found</div>
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        {members.map((member) => {
          const status = getSubscriptionStatus(member)
          const latest = getLatestSubscription(member)
          const isExpanded = expanded === member.id

          return (
            <div key={member.id} className="border border-gray-200 dark:border-white/10 overflow-hidden">
              <div
                onClick={() => setExpanded(isExpanded ? null : member.id)}
                className={`px-6 py-4 flex items-center gap-4 cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-white/5 ${isExpanded ? 'bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/10' : ''}`}
              >
                <div className="w-10 h-10 rounded-full overflow-hidden bg-green-900/50 border border-green-700/50 flex items-center justify-center text-white font-black text-sm shrink-0">
                  {member.profile_photo ? (
                    <img src={member.profile_photo} alt="Profile" className="w-full h-full object-cover" />
                  ) : member.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <span className="font-black text-gray-900 dark:text-white text-sm uppercase tracking-tight">{member.full_name}</span>
                    <span className={`text-xs font-bold uppercase tracking-widest px-2 py-0.5 shrink-0 ${statusStyles[status]}`}>{status}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                    <span className="text-green-600 dark:text-green-400 font-bold tracking-wider">{member.membership_id}</span>
                    <span className="hidden sm:block">{member.chapters?.name || 'No chapter'}</span>
                    {latest && (
                      <span className="hidden md:block">{currency} {latest.amount} · Paid {new Date(latest.payment_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    )}
                  </div>
                </div>

                <div className="hidden lg:block text-xs text-gray-500 shrink-0 text-right">
                  {latest ? (
                    <>
                      <div className="text-gray-400 mb-0.5">Valid until</div>
                      <div className={status === 'paid' ? 'text-green-600 dark:text-green-400 font-bold' : 'text-red-500 font-bold'}>
                        {new Date(latest.valid_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </>
                  ) : (
                    <span className="text-red-500 font-bold">No payment</span>
                  )}
                </div>

                <div className="shrink-0 text-gray-400">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </div>
              </div>

              {isExpanded && (
                <div className="px-6 py-4 bg-gray-50 dark:bg-black/20 flex flex-wrap gap-3 items-center">
                  <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mr-2">Actions:</div>
                  <button onClick={() => onRecordPayment(member)} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors">
                    {status === 'paid' ? 'Update Payment' : 'Record Payment'}
                  </button>
                  {latest && status === 'paid' && (
                    <button
                      onClick={() => setConfirmModal({ type: 'inactive', subscriptionId: latest.id, memberName: member.full_name, title: 'Mark as Inactive', message: `This will immediately revoke active status for ${member.full_name}. Their membership card will no longer show as valid.`, confirmLabel: 'Mark Inactive', confirmColor: 'bg-yellow-600 hover:bg-yellow-700' })}
                      className="border border-yellow-200 dark:border-yellow-900/50 hover:border-yellow-500 dark:hover:border-yellow-600 text-yellow-600 hover:text-yellow-500 px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors"
                    >
                      Mark Inactive
                    </button>
                  )}
                  {latest && (
                    <button
                      onClick={() => setConfirmModal({ type: 'delete', subscriptionId: latest.id, memberName: member.full_name, title: 'Delete Payment Record', message: `This will permanently delete the payment record for ${member.full_name}. They will show as unpaid. This cannot be undone.`, confirmLabel: 'Delete Payment', confirmColor: 'bg-red-600 hover:bg-red-700' })}
                      className="border border-red-200 dark:border-red-900/50 hover:border-red-500 dark:hover:border-red-600 text-red-500 hover:text-red-400 px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors"
                    >
                      Delete Payment
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {confirmModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-white/10 p-8 w-full max-w-md">
            <h2 className="font-black text-xl uppercase tracking-tight mb-3 text-gray-900 dark:text-white">{confirmModal.title}</h2>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">{confirmModal.message}</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmModal(null)} className="flex-1 border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/30 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors">Cancel</button>
              <button onClick={handleConfirm} className={`flex-1 text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors ${confirmModal.confirmColor}`}>{confirmModal.confirmLabel}</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}